/**
 * Access Control Utilities
 *
 * Helper functions to apply merchant and location filtering to Supabase queries
 * Per ADMIN_PANEL_OVERVIEW.md Section 7.1 - Multi-Tenant Data Isolation
 */

export interface AccessControl {
  merchantId: string | null;
  /**
   * Locations this user is scoped to. `null` or an empty array means
   * "all locations in the merchant" (merchant-wide access). A non-empty
   * array means the user is restricted to exactly those locations.
   */
  locationIds: string[] | null;
  isPlatformAdmin: boolean;
}

/**
 * Apply access control filtering to a Supabase query
 *
 * Rules:
 * - Platform admins: No filtering (see all data)
 * - Location-scoped users: Filter by merchant_id AND location_id IN (...)
 * - Merchant-wide users (no location restriction): Filter by merchant_id only
 *
 * @param query - The Supabase query builder
 * @param accessControl - User's access control info
 * @param options - Optional column overrides for joined queries
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyAccessControl(
  query: any,
  accessControl: AccessControl | null,
  options?: {
    merchantIdColumn?: string;
    locationIdColumn?: string;
  }
): any {
  // No access control provided - return query as-is
  if (!accessControl) {
    return query;
  }

  // Platform admins see all data
  if (accessControl.isPlatformAdmin) {
    return query;
  }

  const merchantColumn = options?.merchantIdColumn || 'merchant_id';
  const locationColumn = options?.locationIdColumn || 'location_id';

  // No merchant access - this shouldn't happen in production.
  // Return a query that matches nothing.
  if (!accessControl.merchantId) {
    console.error('User has no merchant access - this should not happen');
    return query.eq(merchantColumn, '00000000-0000-0000-0000-000000000000');
  }

  query = query.eq(merchantColumn, accessControl.merchantId);

  // Location-scoped users: restrict to their assigned locations.
  //
  // GUARD: only apply `.in()` for a NON-EMPTY array. `null` or an empty
  // array means "all locations", which is expressed as the merchant-only
  // filter above. Calling `.in(column, [])` would compile to `IN ()` and
  // match NOTHING, silently hiding all data from merchant-wide users.
  const locationIds = accessControl.locationIds;
  if (locationIds && locationIds.length > 0) {
    query = query.in(locationColumn, locationIds);
  }

  return query;
}

/**
 * Check if user can access a specific merchant
 */
export function canAccessMerchant(
  accessControl: AccessControl | null,
  merchantId: string
): boolean {
  if (!accessControl) return false;
  if (accessControl.isPlatformAdmin) return true;
  return accessControl.merchantId === merchantId;
}

/**
 * Check if user can access a specific location
 */
export function canAccessLocation(
  accessControl: AccessControl | null,
  locationId: string,
  merchantId?: string
): boolean {
  if (!accessControl) return false;
  if (accessControl.isPlatformAdmin) return true;

  // Check merchant access
  if (merchantId && accessControl.merchantId !== merchantId) return false;

  // If user is location-scoped, check the location is in their set
  if (accessControl.locationIds && accessControl.locationIds.length > 0) {
    return accessControl.locationIds.includes(locationId);
  }

  // Merchant-wide users (no location restriction) can access all locations
  return true;
}
